// store/features/billing.feature.tsx
import { AvailablePlans, FeatureAccess, UserBillingProfile } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import { expectedOrganizationHeaders } from '@/store/expected-organization-headers';

export interface CreateCheckoutSessionRequest {
  organizationId: string;
  price_id: string;
  // V2-only — total seats for the new sub. Defaults to base_seats.
  seats?: number;
  credit_addon_price_id?: string;
  // v4: the extra credits chosen; the add-on is one per-credit price.
  credit_addon_credits?: number;
  // v4 Pro: extra senders (1 mailbox + 1 LinkedIn account each).
  sender_addon_price_id?: string;
  sender_addon_quantity?: number;
  accept_early_conversion?: boolean;
  // Card-backed trial: end the trial and bill today (default: change the
  // plan the trial becomes, nothing charged).
  start_now?: boolean;
}

export interface CreateCheckoutSessionResponse {
  sessionId?: string;
  url?: string;
  action: 'checkout' | 'modified' | 'scheduled';
  effective_at?: number;
  /** Present for a trial win-back checkout. The gate refuses to redirect
   * unless the server explicitly confirms the advertised discount. */
  discount_applied?: boolean;
  organization_id?: string;
}

export interface BillingPortalResponse {
  url: string;
  /** Optional: the echo is a newer backend contract. Staging still answers
   * `{ url }` alone, so a required field here would brick the portal on any
   * deploy where the frontend lands before the backend. */
  organization_id?: string;
}

export interface OrganizationScope {
  organizationId: string;
}

// Cancel flow. `state` is what actually happened inside Stripe's portal:
// `cancelled` = a cancel is scheduled or the sub is gone, `saved` = they took
// the retention discount and are still active, `unchanged` = they backed out.
// All three are reachable, because accepting the offer still redirects back.
export interface CancelIntentResponse {
  state: 'cancelled' | 'saved' | 'unchanged';
  feedback: string | null;
  plan_name: string | null;
  period_end: string | null;
}

/** `already_renewing`: no cancellation was scheduled, so nothing changed. */
export interface RenewPlanResponse {
  status: 'renewed' | 'already_renewing';
  organization_id?: string;
}

export interface CancellationFeedbackRequest {
  intent_id: string;
  free_text: string;
  // Which branched prompt was on screen, so a verbatim can be read against
  // the question that produced it.
  prompt?: string;
}

export interface PreviewPlanChangeRequest {
  organizationId: string;
  price_id: string;
  seats?: number;
  credit_addon_price_id?: string;
  credit_addon_credits?: number;
  sender_addon_price_id?: string;
  sender_addon_quantity?: number;
}

export interface PreviewPlanChangeResponse {
  organization_id?: string;
  direction: 'upgrade' | 'downgrade' | 'same_tier';
  current_plan_name: string | null;
  new_plan_name: string | null;
  proration_amount: number | null;
  next_billing_date: string | null;
  new_monthly_credits: number;
  current_credits_remaining: number;
  /** Months unused credits roll over for; 0 is none. */
  credits_rollover_months: number;
  /** Never sent by the API (see credits_rollover_months); read only by the
   *  earlier Plans layout, kept as it was. */
  credits_carry_over?: boolean;
  // V2 seat fields — null when the previewed price isn't a V2 tier.
  new_base_seats: number | null;
  new_extra_seat_count: number | null;
  new_total_seats: number | null;
  member_count_exceeds_new: boolean | null;
}

export interface AddSeatsRequest {
  organizationId: string;
  delta: number;
  target_seats?: number;
  proration_date?: number;
}

export interface AddSeatsResponse {
  organization_id?: string;
  seats_paid: number;
  proration_amount: number;
  next_billing_date: string | null;
  prorated_amount_cents?: number;
  clerk_cap_confirmed?: boolean;
}

export interface SeatChangePreview {
  organization_id?: string;
  prorated_amount_cents: number;
  next_billing_date: string | null;
  seats_paid: number;
  target_seats: number;
  proration_date?: number;
}

export interface RemoveSeatsRequest {
  organizationId: string;
  delta: number;
}

export interface RemoveSeatsResponse {
  organization_id?: string;
  seats_paid: number;
  credit_amount: number;
}

export interface CreditTopupRequest {
  organizationId: string;
  credits: number;
}

export interface CreditTopupResponse {
  organization_id?: string;
  sessionId?: string;
  url?: string;
}

/** Auto top-up: buy `credits` on the saved card once the balance drops
 * below `threshold`. `last_error` says why it last failed or paused. */
export interface AutoTopupSettings {
  enabled: boolean;
  threshold: number;
  credits: number;
}

export interface AutoTopupResponse extends AutoTopupSettings {
  last_error: string | null;
  organization_id?: string;
}

export interface SmartColumnCreditCost {
  column_type: string;
  credits: number; // Can be fractional (e.g., 0.1, 0.5, 1.0)
  display_name: string;
  description?: string;
}

export interface SmartColumnCreditCostsResponse {
  costs: SmartColumnCreditCost[];
  last_updated: string;
  organization_id?: string;
}

/** Reject a response that identifies a different tenant. Older endpoints do
 * not all echo organization_id yet, so the expected-org request header remains
 * the primary server-side guard while this check protects every response that
 * supports the stronger contract.
 *
 * The rule is deliberately one-sided and applies to EVERY endpoint in this
 * file: a PRESENT-and-mismatched id is a cross-tenant response and throws; a
 * MISSING id passes. Demanding the echo would break every billing call the
 * moment the frontend deploys ahead of the backend. */
const assertOrganizationResponse = <T extends object>(
  response: T,
  organizationId: string
): T => {
  const returnedOrganizationId = (response as { organization_id?: unknown })
    .organization_id;
  if (
    typeof returnedOrganizationId === 'string' &&
    returnedOrganizationId !== organizationId
  ) {
    throw new Error('Billing response did not match the active organization.');
  }
  return response;
};

export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'BillingProfile',
    'BillingPortal',
    'Plans',
    'CreditCosts',
    'FeatureAccess',
    'AutoTopup',
  ],
  endpoints: (builder) => ({
    // Get available plans
    getPlans: builder.query<AvailablePlans, OrganizationScope>({
      query: ({ organizationId }) => ({
        url: '/billing/plans',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: AvailablePlans,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'Plans', id: organizationId },
      ],
    }),

    // Get user billing profile
    getBillingProfile: builder.query<UserBillingProfile, OrganizationScope>({
      query: ({ organizationId }) => ({
        url: '/billing/profile',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: UserBillingProfile,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'BillingProfile', id: organizationId },
      ],
    }),

    // Create a Stripe checkout session pinned to the active organization.
    createCheckoutSession: builder.mutation<
      CreateCheckoutSessionResponse,
      CreateCheckoutSessionRequest
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/billing/checkout',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      transformResponse: (
        response: CreateCheckoutSessionResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['BillingProfile', 'Plans'],
    }),

    getSeatChangePreview: builder.query<SeatChangePreview, AddSeatsRequest>({
      query: ({ organizationId, delta }) => ({
        url: '/billing/seats/preview',
        params: { delta },
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: SeatChangePreview,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      keepUnusedDataFor: 0,
    }),

    activatePaidSeats: builder.mutation<AddSeatsResponse, OrganizationScope>({
      query: ({ organizationId }) => ({
        url: '/billing/seats/activate',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: AddSeatsResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['BillingProfile', 'Plans'],
    }),

    addSeats: builder.mutation<AddSeatsResponse, AddSeatsRequest>({
      query: ({ organizationId, ...body }) => ({
        url: '/billing/seats/add',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      transformResponse: (
        response: AddSeatsResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['BillingProfile', 'Plans'],
    }),

    removeSeats: builder.mutation<RemoveSeatsResponse, RemoveSeatsRequest>({
      query: ({ organizationId, ...body }) => ({
        url: '/billing/seats/remove',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      transformResponse: (
        response: RemoveSeatsResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['BillingProfile', 'Plans'],
    }),

    // Get billing portal URL
    getBillingPortal: builder.query<BillingPortalResponse, OrganizationScope>({
      query: ({ organizationId }) => ({
        url: '/billing/portal',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: BillingPortalResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'BillingPortal', id: organizationId },
      ],
    }),

    // Cancel-plan surfaces (staging) — intentionally NOT org-scoped: their
    // backend endpoints predate the org-scoping refactor. Revisit if the
    // cleanse-api -be branch scopes /billing/portal/cancel + /billing/cancel-intent.
    getCancelPortal: builder.query<BillingPortalResponse, void>({
      query: () => '/billing/portal/cancel',
      keepUnusedDataFor: 0,
    }),

    getCancelIntent: builder.query<CancelIntentResponse, string>({
      query: (intentId) => `/billing/cancel-intent/${intentId}`,
      keepUnusedDataFor: 0,
    }),

    submitCancellationFeedback: builder.mutation<
      void,
      CancellationFeedbackRequest
    >({
      query: (body) => ({
        url: '/feedback/cancellation',
        method: 'POST',
        body,
      }),
    }),

    // Get feature access for current tier
    getFeatureAccess: builder.query<FeatureAccess, OrganizationScope>({
      // Gates and pages unmount and mount as the rep moves around; the read
      // outlives that, so coming back never waits on it (focus and the
      // five-minute poll keep it current).
      keepUnusedDataFor: 600,
      query: ({ organizationId }) => ({
        url: '/billing/feature-access',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (response: FeatureAccess, _meta, { organizationId }) =>
        assertOrganizationResponse(response, organizationId),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'FeatureAccess', id: organizationId },
      ],
    }),

    previewPlanChange: builder.mutation<
      PreviewPlanChangeResponse,
      PreviewPlanChangeRequest
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/billing/preview-plan-change',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      transformResponse: (
        response: PreviewPlanChangeResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
    }),

    createCreditTopup: builder.mutation<
      CreditTopupResponse,
      CreditTopupRequest
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/billing/credit-topup',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      transformResponse: (
        response: CreditTopupResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['BillingProfile'],
    }),

    getAutoTopup: builder.query<AutoTopupResponse, OrganizationScope>({
      query: ({ organizationId }) => ({
        url: '/billing/auto-topup',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: AutoTopupResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'AutoTopup', id: organizationId },
      ],
    }),

    updateAutoTopup: builder.mutation<
      AutoTopupResponse,
      OrganizationScope & AutoTopupSettings
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/billing/auto-topup',
        method: 'PUT',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      transformResponse: (
        response: AutoTopupResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'AutoTopup', id: organizationId },
      ],
    }),

    getSmartColumnCreditCosts: builder.query<
      SmartColumnCreditCostsResponse,
      OrganizationScope
    >({
      query: ({ organizationId }) => ({
        url: '/billing/smart-column-costs',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: SmartColumnCreditCostsResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'CreditCosts', id: organizationId },
      ],
    }),

    activateTrial: builder.mutation<
      CreateCheckoutSessionResponse,
      OrganizationScope & { accept_early_conversion?: boolean }
    >({
      query: ({ organizationId, ...body }) => ({
        url: '/billing/trial/activate',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
        body,
      }),
      transformResponse: (
        response: CreateCheckoutSessionResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
    }),
    cancelTrialRenewal: builder.mutation<
      { status: string; organization_id?: string },
      OrganizationScope
    >({
      query: ({ organizationId }) => ({
        url: '/billing/trial/cancel-renewal',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: { status: string; organization_id?: string },
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['FeatureAccess'],
    }),
    // Undoes a paid plan's scheduled cancellation: it renews as before. A 410
    // (the plan has already ended) invalidates the tags too, so the page
    // reloads the plan the workspace is on now.
    renewPlan: builder.mutation<RenewPlanResponse, OrganizationScope>({
      query: ({ organizationId }) => ({
        url: '/billing/renew',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: RenewPlanResponse,
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['BillingProfile', 'Plans', 'FeatureAccess'],
      // The refetch takes a second or two of Stripe calls. Clear the end date
      // now, so "Ends …" and the Renew button don't linger for another click.
      async onQueryStarted({ organizationId }, { dispatch, queryFulfilled }) {
        const clearEndsOn = () =>
          dispatch(
            billingApi.util.updateQueryData(
              'getBillingProfile',
              { organizationId },
              (draft) => {
                draft.ends_on = null;
              }
            )
          );
        try {
          await queryFulfilled;
          clearEndsOn();
        } catch (e) {
          if ((e as { error?: { status?: unknown } }).error?.status === 410)
            clearEndsOn();
        }
      },
    }),
    // Free Trial v1: one-click Free from the post-trial choose-your-plan
    // gate. Invalidating FeatureAccess clears `trial.must_choose_plan`, which
    // dismisses the gate without a reload.
    chooseFreePlan: builder.mutation<
      {
        status: string;
        plan_chosen: string | null;
        organization_id?: string;
      },
      OrganizationScope
    >({
      query: ({ organizationId }) => ({
        url: '/billing/trial/choose-free',
        method: 'POST',
        headers: expectedOrganizationHeaders(organizationId),
      }),
      transformResponse: (
        response: {
          status: string;
          plan_chosen: string | null;
          organization_id?: string;
        },
        _meta,
        { organizationId }
      ) => assertOrganizationResponse(response, organizationId),
      invalidatesTags: ['FeatureAccess', 'Plans', 'BillingProfile'],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useLazyGetPlansQuery,
  useGetBillingProfileQuery,
  useCreateCheckoutSessionMutation,
  usePreviewPlanChangeMutation,
  useCreateCreditTopupMutation,
  useGetAutoTopupQuery,
  useUpdateAutoTopupMutation,
  useLazyGetBillingPortalQuery,
  useLazyGetCancelPortalQuery,
  useGetCancelIntentQuery,
  useSubmitCancellationFeedbackMutation,
  useGetFeatureAccessQuery,
  useGetSmartColumnCreditCostsQuery,
  useAddSeatsMutation,
  useGetSeatChangePreviewQuery,
  useActivatePaidSeatsMutation,
  useRemoveSeatsMutation,
  useActivateTrialMutation,
  useCancelTrialRenewalMutation,
  useRenewPlanMutation,
  useChooseFreePlanMutation,
} = billingApi;
